import Flex from "common/Boxes/Flex";
import Text from "common/Typography/Text";
import LineChartOutlined from "@ant-design/icons/LineChartOutlined";
import UnorderedListOutlined from "@ant-design/icons/UnorderedListOutlined";
import React from "react";
import styled, { css, useTheme } from "styled-components";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import FreeTextModal from "features/tradingTerminal/FreeText/FreeTextModal";
import SummaryModal from "features/tradingTerminal/Summary/SummaryModal";
import useSummary from "@shared/features/tradingTerminal/common/hooks/useSummary";
import {
  ConfirmButton,
  IconButton,
} from "features/tradingTerminal/common/styles";
import { openMessage } from "utils/misc";
import TradingTerminalChart from "features/tradingTerminal/common/TradingTerminalChart";
import Drawer from "common/Drawers/Drawer";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import Tooltip from "common/Tooltips/Tooltip";
import { StyledDefaultButton } from "common/Buttons/Button.tsx";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import AutoSizeText from "common/Text/AutoSizeText.tsx";
import { ChartLine, ListChecks } from "lucide-react";
import LookIcon from "common/Icons/LookIcon";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook";

interface SummaryProps {
  isOrder?: boolean;
}

const Summary = ({ isOrder }: SummaryProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADING_TERMINAL);
  const theme = useTheme();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const {
    actionButtonTitle,
    onActionButtonClick,
    actionButtonDisabled,
    ordersSummaryModalOpen,
    isConfirm,
    onIconButtonClick,
    freeTextModalOpen,
    setFreeTextModalOpen,
    isMobile,
    chartDrawerOpen,
    setChartDrawerOpen,
    iconTooltipTitle,
    closeSummaryModal,
    isLoading,
    tradeAmountData,
    existingAmountData,
    availableAmountData,
    showExistingAmount,
  } = useSummary(openMessage, isOrder);
  // New look: the loading placeholder is as tall as the rows (22px, 8px apart),
  // the gap above the actions and the 40px actions.
  const newLookMinHeight =
    (availableAmountData.shouldDisplay ? 52 : 22) +
    (isBottomNavigator ? 12 : 16) +
    40;
  // New look: the USD equivalent reads as "≈ 250 USD" instead of "(250 USD)".
  const formatUsdValue = (usdValue: string) =>
    theme.isNewLook ? usdValue.replace(/^\((.*)\)$/, "≈ $1") : usdValue;

  return (
    <LoadingSpinnerOverlay
      isLoading={isLoading}
      hideContentWhileLoading={true}
      wrap={true}
      backgroundColor={
        theme.colors[isBottomNavigator ? "layoutBgColor" : "cardBgColor"]
      }
      wrapStyle={{
        minHeight: theme.isNewLook
          ? newLookMinHeight
          : availableAmountData.shouldDisplay
          ? 116
          : 78,
      }}
    >
      {availableAmountData.shouldDisplay && (
        <RowWrapper>
          <RowTitle>{availableAmountData.title}</RowTitle>
          <RowValueWrapper>
            <RowValue>{availableAmountData.value}</RowValue>
            <RowUsdValue>
              {formatUsdValue(availableAmountData.usdValue)}
            </RowUsdValue>
          </RowValueWrapper>
        </RowWrapper>
      )}
      <RowWrapper>
        <RowTitle>{tradeAmountData.title}</RowTitle>
        <RowValueWrapper>
          <RowValue>{tradeAmountData.value}</RowValue>
          <RowUsdValue>{formatUsdValue(tradeAmountData.usdValue)}</RowUsdValue>
        </RowValueWrapper>
      </RowWrapper>
      {showExistingAmount && (
        <RowWrapper>
          <RowTitle>{existingAmountData.title}</RowTitle>
          <RowValueWrapper>
            <RowValue>{existingAmountData.value}</RowValue>
            <RowUsdValue>
              {formatUsdValue(existingAmountData.usdValue)}
            </RowUsdValue>
          </RowValueWrapper>
        </RowWrapper>
      )}
      <ButtonsWrapper $isBottomNavigator={isBottomNavigator}>
        <Tooltip title={iconTooltipTitle}>
          <IconButton onClick={onIconButtonClick}>
            {isMobile ? (
              <LookIcon
                classic={<LineChartOutlined />}
                newLook={
                  <ChartLine size={18} strokeWidth={NEW_LOOK_ICON_STROKE} />
                }
              />
            ) : (
              <LookIcon
                classic={<UnorderedListOutlined />}
                newLook={
                  <ListChecks size={18} strokeWidth={NEW_LOOK_ICON_STROKE} />
                }
              />
            )}
          </IconButton>
        </Tooltip>
        {!isOrder && (
          <StyledDefaultButton
            onClick={() => setFreeTextModalOpen(true)}
            style={{ flex: 1 }}
            $size="l"
          >
            <AutoSizeText
              style={{
                color: theme.isNewLook
                  ? theme.colors.primaryText
                  : theme.colors.primaryBlue,
              }}
            >
              {t("buttons.free_text")}
            </AutoSizeText>
          </StyledDefaultButton>
        )}
        <ConfirmButton
          style={{ flex: 1 }}
          onClick={onActionButtonClick}
          $disabled={actionButtonDisabled}
        >
          <AutoSizeText style={{ color: theme.colors.buttonText }}>
            {actionButtonTitle}
          </AutoSizeText>
        </ConfirmButton>
      </ButtonsWrapper>
      <FreeTextModal
        visible={freeTextModalOpen}
        closeModal={() => setFreeTextModalOpen(false)}
      />
      <SummaryModal
        visible={ordersSummaryModalOpen}
        closeModal={closeSummaryModal}
        isConfirm={isConfirm}
      />
      <StyledDrawer
        open={chartDrawerOpen}
        onClose={() => setChartDrawerOpen(false)}
        destroyOnHidden={false}
      >
        <TradingTerminalChart />
      </StyledDrawer>
    </LoadingSpinnerOverlay>
  );
};

export default React.memo(Summary);

const ButtonsWrapper = styled(Flex)<{ $isBottomNavigator?: boolean }>`
  grid-gap: 8px;
  ${(p) =>
    p.theme.isNewLook &&
    `
    /* The space above the actions equals the footer's padding below them. */
    margin-top: ${p.$isBottomNavigator ? 12 : 16}px;
    /* Standard 40px controls (the large size read as too heavy here). */
    .ant-btn {
      height: 40px !important;
      min-height: 40px !important;
      font-size: 14px;
    }
    .ant-btn p {
      font-size: 14px !important;
      font-weight: 500;
    }
    .ant-btn-primary p {
      font-weight: 600;
    }
    /* One primary action; the secondary ones are quiet outlined pills. */
    .ant-btn-default {
      background: transparent !important;
      border: 1px solid ${p.theme.colors.lightBorderGrey} !important;
      color: ${p.theme.colors.primaryText} !important;
      box-shadow: none;
    }
    .ant-btn-default:not(:disabled):hover {
      background: ${p.theme.colors.cardLightBgColor} !important;
      border-color: ${p.theme.colors.mediumBorderGrey} !important;
    }
  `}
`;
const RowWrapper = styled(Flex)`
  margin-bottom: 16px;
  align-items: flex-start;
  ${(p) =>
    p.theme.isNewLook &&
    `
    margin-bottom: 8px;
    align-items: baseline;
  `}
`;
// New look: the label role, the amount as a figure and its USD as meta.
const RowTitle = styled(Text)`
  white-space: nowrap;
  margin-right: 16px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      ${newLookType.label};
      color: ${getNewLookTokens(p.theme).secondary};
    `}
`;
const RowValueWrapper = styled(Flex)`
  text-align: right;
  align-items: baseline;
  flex-wrap: wrap;
  justify-content: flex-end;
  ${(p) => p.theme.isNewLook && `column-gap: 4px;`}
`;
const RowValue = styled(Text)`
  margin-right: 4px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      margin-right: 0;
      ${newLookType.figure};
      /* A summary line, not a headline: body size, medium weight. */
      font-size: 14px;
      font-weight: 500;
      color: ${getNewLookTokens(p.theme).ink};
    `}
`;
const RowUsdValue = styled(Text)`
  font-size: ${(p) => p.theme.typography.fontSizes.S};
  ${(p) =>
    p.theme.isNewLook &&
    css`
      ${newLookType.meta};
      color: ${getNewLookTokens(p.theme).muted};
      &:empty {
        display: none;
      }
    `}
`;
const StyledDrawer = styled(Drawer)`
  .ant-drawer-body {
    padding: 36px 0 8px !important;
  }
`;
